// In-app notification island: receives `notify` events from the realtime stream, filters them by the
// person's preferences and shows them one at a time at the top centre of the window. Inside the
// desktop app the Electron shell shows them at screen level instead (unless that's turned off).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import type { AppNotification, NotificationKind, NotificationPrefs } from '@mailpilot/shared';
import { defaultNotificationPrefs } from '@mailpilot/shared';
import { api } from './api';
import { bridge } from './desktop';
import { qk } from './keys';
import { onNotification } from './notificationBus';
import { playTone } from './sound';
import { useMe } from '../hooks/useMe';
import { NotificationIsland } from '../components/layout/NotificationIsland';

export const KIND_CATEGORY: Record<NotificationKind, keyof Pick<NotificationPrefs, 'newMail' | 'ai' | 'automation' | 'system'>> = {
  mail: 'newMail',
  ai_draft: 'ai',
  ai_analysis: 'ai',
  auto_reply: 'automation',
  reminder: 'automation',
  send_failed: 'system',
  account: 'system',
  workspace: 'system',
};

const MAX_QUEUE = 5;

/** Fold a newer mail notification into the one on screen ("3 new emails · Latest from Dana"). */
function mergeMail(head: AppNotification, n: AppNotification): AppNotification {
  const count = head.count + n.count;
  // Grouped server notifications read "N new emails / From A and B / Latest: subject".
  const sender = n.count > 1 ? n.body.replace(/^From /, '') : n.title;
  const subject = n.count > 1 ? (n.detail ?? '').replace(/^Latest: /, '') : n.body;
  return { ...n, id: head.id, count, title: `${count} new emails`, body: `Latest from ${sender}`, detail: subject || null };
}

interface NotificationsCtx {
  /** Notification currently in the island (null = idle). */
  current: AppNotification | null;
  queued: number;
  dismiss: () => void;
  /** Show something in the island right now (bypasses category filters). */
  show: (n: AppNotification) => void;
  /** Send a sample through the server → realtime → island pipeline. */
  preview: (kind: NotificationKind) => Promise<void>;
  unseenCount: number;
  prefs: NotificationPrefs;
}

const Ctx = createContext<NotificationsCtx | null>(null);

export function useNotificationHistory(enabled = true) {
  return useQuery({ queryKey: qk.notifications, queryFn: () => api('GET /api/notifications'), enabled, staleTime: 30_000 });
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const me = useMe().data;
  const prefs = me?.settings.notifications ?? defaultNotificationPrefs();
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const desktop = bridge();
  const screenLevel = !!desktop && prefs.desktopScreenIsland;

  const [queue, setQueue] = useState<AppNotification[]>([]);

  const enqueue = useCallback((n: AppNotification) => {
    setQueue((q) => {
      // A burst of mail for the same account updates the visible card instead of queueing.
      const head = q[0];
      if (head && n.kind === 'mail' && head.kind === 'mail' && head.mailboxId === n.mailboxId && head.id !== n.id) {
        return [mergeMail(head, n), ...q.slice(1)];
      }
      if (q.some((x) => x.id === n.id)) return q;
      const next = [...q, n];
      return next.length > MAX_QUEUE ? [next[0], ...next.slice(-(MAX_QUEUE - 1))] : next;
    });
  }, []);

  const show = useCallback(
    (n: AppNotification) => {
      if (desktop?.showIsland && prefsRef.current.desktopScreenIsland) {
        void desktop.showIsland(n).catch(() => enqueue(n));
        return;
      }
      enqueue(n);
    },
    [desktop, enqueue],
  );

  useEffect(
    () =>
      onNotification((n) => {
        const p = prefsRef.current;
        if (!p.island || !p[KIND_CATEGORY[n.kind]]) return;
        // Already reading that conversation: nothing to announce.
        if (n.threadId && document.visibilityState === 'visible' && pathRef.current.endsWith(`/${n.threadId}`)) return;
        if (document.visibilityState === 'hidden' && !desktop && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            const sys = new Notification(n.title, { body: n.detail ? `${n.body}\n${n.detail}` : n.body, tag: `mp-${n.id}` });
            sys.onclick = () => window.focus();
          } catch {
            /* ignore */
          }
        }
        // The desktop shell shows server notifications at screen level by itself.
        if (screenLevel) return;
        enqueue(n);
      }),
    [desktop, screenLevel, enqueue],
  );

  // Chime as each notification takes the stage (not when it was queued behind another one).
  const shown = queue[0];
  useEffect(() => {
    if (shown && prefsRef.current.sound) playTone(shown.tone ?? 'chime');
  }, [shown?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = useCallback(() => setQueue((q) => q.slice(1)), []);

  const preview = useCallback(async (kind: NotificationKind) => {
    await api('POST /api/notifications/test', { body: { kind } });
  }, []);

  const history = useNotificationHistory(!!me);
  const seenAt = history.data?.seenAt ? Date.parse(history.data.seenAt) : 0;
  const unseenCount = (history.data?.notifications ?? []).filter((n) => Date.parse(n.createdAt) > seenAt).length;

  const value = useMemo<NotificationsCtx>(
    () => ({ current: queue[0] ?? null, queued: Math.max(0, queue.length - 1), dismiss, show, preview, unseenCount, prefs }),
    [queue, dismiss, show, preview, unseenCount, prefs],
  );
  return (
    <Ctx.Provider value={value}>
      {children}
      <NotificationIsland current={value.current} queued={value.queued} durationSeconds={prefs.durationSeconds} look={prefs} onDone={dismiss} />
    </Ctx.Provider>
  );
}

export function useNotifications(): NotificationsCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useNotifications outside NotificationProvider');
  return c;
}
